import React, { useEffect, useMemo, useState, useCallback } from "react";
import MarketingLayout from "@/components/layout/MarketingLayout";
import api, { API } from "@/lib/api";
import useScrollReveal from "@/lib/useScrollReveal";
import { X, ArrowUpRight, Play } from "lucide-react";
import WeddingStoriesSection from "@/components/WeddingStories";

const categories = [
  { id: "all", label: "All" },
  { id: "pakistan", label: "Pakistan" },
  { id: "international", label: "International" },
];

function getYouTubeEmbedUrl(inputUrl = "") {
  if (!inputUrl) return "https://www.youtube.com/embed/dQw4w9WgXcQ";
  if (inputUrl.length === 11 && !inputUrl.includes("/")) {
    return `https://www.youtube.com/embed/${inputUrl}?autoplay=1`;
  }
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = inputUrl.match(regExp);
  const videoId = match && match[2].length === 11 ? match[2] : "dQw4w9WgXcQ";
  return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
}

function getYouTubeThumbnail(inputUrl = "") {
  if (!inputUrl) return "https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=1200&auto=format&fit=crop";
  if (inputUrl.length === 11 && !inputUrl.includes("/")) {
    return `https://img.youtube.com/vi/${inputUrl}/hqdefault.jpg`;
  }
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = inputUrl.match(regExp);
  const videoId = match && match[2].length === 11 ? match[2] : null;
  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=1200&auto=format&fit=crop";
}

// Lightweight fade-in <img> wrapper: no extra dependency needed.
// Shows a soft pulsing placeholder until the real image finishes loading,
// which removes the "pop-in" jank and makes loading feel smoother.
function SmartImage({ src, alt, className = "", imgClassName = "", style, onClick }) {
  const [loaded, setLoaded] = useState(false);
  const handleLoad = useCallback(() => setLoaded(true), []);

  return (
    <div
      className={`relative overflow-hidden bg-neutral-200/60 ${!loaded ? "animate-pulse" : ""} ${className}`}
      style={style}
      onClick={onClick}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        className={`${imgClassName} transition-opacity duration-500 ease-out ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}

// Skeleton block shown in place of the grid while the initial /media
// request is still in flight, so the page never flashes a false
// "no photos yet" message before the real data has arrived.
function SkeletonBlock({ className = "", style }) {
  return <div className={`animate-pulse bg-neutral-200/60 ${className}`} style={style} />;
}

// Shows an already-cached small version instantly, then swaps to the
// high-res version once it's ready — used in the lightbox so opening a
// photo never shows a blank/gray screen while the large variant is
// generated for the first time.
function ProgressiveImage({ lowSrc, highSrc, alt, className = "", imgClassName = "" }) {
  const [highLoaded, setHighLoaded] = useState(false);

  useEffect(() => {
    setHighLoaded(false);
    let cancelled = false;
    const img = new window.Image();
    img.src = highSrc;
    img.onload = () => {
      if (!cancelled) setHighLoaded(true);
    };
    return () => {
      cancelled = true;
    };
  }, [highSrc]);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <img src={lowSrc} alt={alt} className={`${imgClassName} absolute inset-0`} />
      <img
        src={highSrc}
        alt={alt}
        className={`${imgClassName} absolute inset-0 transition-opacity duration-500 ease-out ${
          highLoaded ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}

export default function Portfolio() {
  const [active, setActive] = useState("all");
  const [mediaItems, setMediaItems] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/media", { params: { visibility: "public" } })
      .then(({ data }) => {
        setMediaItems(Array.isArray(data) ? data : []);
      })
      .catch(() => setMediaItems([]))
      .finally(() => setLoading(false));
  }, []);

  const { videos, images } = useMemo(() => {
    const list = Array.isArray(mediaItems) ? mediaItems : [];
    const uploadedVideos = list
      .filter((m) => m.media_type === "youtube" || m.media_type === "video" || (m.url && m.url.includes("youtube")))
      .map((m) => ({
        id: m.id,
        category: m.category || "Wedding Film",
        region: m.region || "pakistan",
        title: m.title,
        location: m.location || "Pakistan",
        year: m.year || "2026",
        youtubeUrl: m.url || m.youtube_url,
        cover: getYouTubeThumbnail(m.url || m.youtube_url),
      }));

    const uploadedImages = list
      .filter((m) => 
        m.media_type !== "youtube" && 
        m.media_type !== "video" && 
        (!m.url || !m.url.includes("youtube")) && 
        m.category !== "story" &&
        m.category !== "Wedding Story" &&
        (!m.title || !m.title.toLowerCase().includes("story"))
      )
      .map((m) => ({
        id: m.id,
        category: m.category || "Editorial",
        region: m.region || "pakistan",
        title: m.title,
        url: `${API}/media/file/${m.id}`,
        width: m.width || null,
        height: m.height || null,
      }));

    return {
      videos: active === "all" ? uploadedVideos : uploadedVideos.filter((v) => v.region === active),
      images: active === "all" ? uploadedImages : uploadedImages.filter((img) => img.region === active),
    };
  }, [mediaItems, active]);

  useScrollReveal([videos.length, images.length, active]);

  return (
    <MarketingLayout>
      <section className="pt-20 pb-12 md:pt-28 bg-olive-cream border-b border-olive-soft" data-testid="portfolio-hero">
        <div className="container-x">
          <div className="overline text-primary mb-6 flex items-center gap-3 fade-up">
            <span className="w-10 h-px bg-primary" /> Portfolio Archive
          </div>
          <div className="flex flex-wrap items-end justify-between gap-8 mb-10 fade-up">
            <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] max-w-3xl font-semibold tracking-tight">
              Cinematic stories, <span className="italic text-primary">frame by frame.</span>
            </h1>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 border-t border-olive-soft pt-6">
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                className={`px-5 py-2.5 text-xs font-semibold tracking-[0.22em] uppercase transition-all duration-300 border ${
                  active === c.id ? "bg-primary text-primary-foreground border-primary" : "border-olive-soft text-primary hover:bg-primary/10"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 1: Stills & Editorial Photos */}
      <section className="py-20 bg-background" data-testid="portfolio-photos-section">
        <div className="w-full px-2 md:px-4">
          <div className="mb-10 max-w-7xl mx-auto px-4">
            <span className="text-[10px] uppercase tracking-[0.35em] text-primary font-semibold flex items-center gap-2 mb-2">
              <span className="w-6 h-[1px] bg-primary" /> Stills & Editorial
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight text-foreground">
              Photographic <span className="italic font-serif text-primary">moments.</span>
            </h2>
          </div>

          {loading ? (
            <div className="max-w-[1920px] mx-auto columns-3 sm:columns-3 lg:columns-3 gap-1 [column-fill:_balance]">
              {[420, 320, 500, 380, 300, 460, 340, 400, 320].map((h, i) => (
                <SkeletonBlock key={i} className="w-full mb-1 break-inside-avoid" style={{ height: h }} />
              ))}
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground text-sm font-mono max-w-7xl mx-auto">
              No photos uploaded yet.
            </div>
          ) : (
            <div className="max-w-[1920px] mx-auto columns-3 sm:columns-3 lg:columns-3 gap-1 [column-fill:_balance]">
              {images.map((m, i) => (
                <div
                  key={m.id || i}
                  className="group cursor-pointer mb-1 break-inside-avoid"
                >
                  <SmartImage
                    src={`${m.url}?w=600`}
                    alt={m.title || "Editorial moment"}
                    onClick={() => setLightbox({ type: "image", ...m })}
                    style={m.width && m.height ? { aspectRatio: `${m.width} / ${m.height}` } : undefined}
                    imgClassName={
                      m.width && m.height
                        ? "w-full h-full object-cover block transition-transform duration-700 ease-out group-hover:scale-105"
                        : "w-full h-auto block transition-transform duration-700 ease-out group-hover:scale-105"
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SECTION 2: Featured Wedding Films */}
      <section className="py-20 bg-[#141F14] text-[#F1F2EC] relative overflow-hidden" data-testid="portfolio-videos-section">
        <div className="container-x relative z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <span className="text-[10px] uppercase tracking-[0.35em] text-[#C9D1B5] font-semibold flex items-center gap-2 mb-2">
                <span className="w-6 h-[1px] bg-[#C9D1B5]" /> Master Cinema
              </span>
              <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight text-white">
                Featured wedding <span className="italic font-serif text-[#C9D1B5]">films.</span>
              </h2>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <SkeletonBlock key={i} className="aspect-[16/10] rounded-[2px]" />
              ))}
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-12 text-[#C9D1B5]/60 text-sm font-mono">No YouTube films added yet.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {videos.map((vid, idx) => (
                <div key={vid.id || idx} onClick={() => setLightbox({ type: "video", ...vid })} className="group relative cursor-pointer bg-[#0e160e] border border-[#283828] overflow-hidden rounded-[2px] shadow-2xl flex flex-col justify-between">
                  <div className="relative aspect-[16/10] overflow-hidden bg-black/40">
                    <SmartImage
                      src={vid.cover || getYouTubeThumbnail(vid.youtubeUrl)}
                      alt={vid.title || "Wedding film"}
                      className="absolute inset-0"
                      imgClassName="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-8 md:w-14 md:h-14 rounded-full bg-[#162016]/90 border border-[#C9D1B5]/40 flex items-center justify-center text-[#C9D1B5]">
                        <Play size={14} className="ml-0.5 md:hidden" fill="currentColor" />
                        <Play size={20} className="ml-1 hidden md:block" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  <div className="p-2.5 md:p-5 bg-[#162016] border-t border-[#283828] flex items-center justify-between gap-2">
                    <h3 className="font-serif text-sm md:text-xl text-white font-medium leading-tight">{vid.title}</h3>
                    <ArrowUpRight size={14} className="text-[#C9D1B5] shrink-0 md:w-4 md:h-4" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SECTION 3: CURATED ARCHIVES (Full Wedding Stories) */}
      <WeddingStoriesSection />

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 md:p-10" onClick={() => setLightbox(null)}>
          <button className="absolute top-6 right-6 text-white w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white hover:text-black transition-colors z-50" onClick={() => setLightbox(null)}>
            <X size={24} strokeWidth={1.5} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {lightbox.type === "video" ? (
              <div className="relative aspect-video w-full shadow-2xl bg-black rounded-[2px] overflow-hidden border border-white/10">
                <iframe src={getYouTubeEmbedUrl(lightbox.youtubeUrl)} title={lightbox.title} className="w-full h-full absolute inset-0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <ProgressiveImage
                lowSrc={`${lightbox.url}?w=600`}
                highSrc={`${lightbox.url}?w=1400`}
                alt={lightbox.title || "Portfolio photo"}
                className="w-full h-[80vh] shadow-2xl rounded-[2px]"
                imgClassName="w-full h-full object-contain"
              />
            )}
          </div>
        </div>
      )}
    </MarketingLayout>
  );
}